import { useMutation, useQueryClient } from '@tanstack/react-query';
import { unApplyFromJob } from '@worker/services/MyJobs/api';
import {
  AppliedJobsResponse,
  JobAssignmentStatus,
  MyJobsTabsEnum,
  WorkerJobCommonResultsResponse,
} from '@worker/types/myJobs.type';
import { useWorkerStore } from '@worker/stores';
import useNotification from '@hooks/useNotification';
import {
  extractErrorMessage,
  removeAppliedJob,
  updateJobApplicationStatus,
} from '@worker/helpers/my-jobs-helpers';
import QueryKeys from '@constants/queryKeys';

/**
 * Hook to un-apply from a job
 * Updates cache on success only - no optimistic updates or query invalidation
 */
export function useUnApplyFromJobMutation(options?: {
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();
  const currentTab = useWorkerStore((state) => state.currentTab);
  const { showSuccessNotification, showErrorNotification } = useNotification();

  return useMutation({
    mutationFn: ({ jobId }: { jobId: number; jobName?: string }) =>
      unApplyFromJob(jobId),
    onSuccess: (_, { jobId, jobName }) => {
      const message = jobName
        ? `You have successfully un-applied from ${jobName}!`
        : 'You have successfully un-applied from this job!';
      showSuccessNotification(message);

      // Update ALL job recommendation caches
      queryClient.setQueriesData<WorkerJobCommonResultsResponse>(
        { queryKey: [QueryKeys.MyJobs.JOB_RECOMMENDATIONS] },
        (data) => {
          if (!data?.results) return data;

          if (currentTab === MyJobsTabsEnum.APPLIED) {
            // APPLIED tab: Remove the job from the list (no longer applied)
            return removeAppliedJob(
              data as AppliedJobsResponse,
              jobId
            ) as WorkerJobCommonResultsResponse;
          }
          // NEW & SAVED tabs: Keep job in list, clear application status
          // Also update assignment status to WORKER_NOT_INTERESTED to disable chat
          // (Backend keeps THJobAssignment but changes status, doesn't delete it)
          const existingResult = data.results.find((r) => r.job?.id === jobId);
          const updatedAssignment = existingResult?.jobAssignment
            ? {
                ...existingResult.jobAssignment,
                status: JobAssignmentStatus.WORKER_NOT_INTERESTED,
              }
            : undefined;

          return updateJobApplicationStatus(
            data,
            jobId,
            null,
            updatedAssignment
          );
        }
      );

      options?.onSuccess?.();
    },
    onError: (err: unknown) => {
      showErrorNotification(
        extractErrorMessage(
          err,
          'Failed to un-apply from job. Please try again.'
        )
      );
    },
  });
}

export default useUnApplyFromJobMutation;
